import { Controller } from '@hotwired/stimulus';
// Extensions `.js` EXPLICITES : esbuild s'en passe, Node non (`node --test`).
import { visibleFocusables } from '../utils/focus_trap.js';
import { forgetPhaseParam } from '../utils/planning_phase_param.js';

/**
 * phase-panel — orchestrates the phase overlay body:
 *   • toggles the in-panel reschedule compose view over the main body
 *   • moves focus into the compose view on open and restores it on cancel
 *   • lazy-loads + opens the overlay when the Gantt selects a leaf phase
 *
 * The Gantt row « + » no longer goes through this controller (refonte planning
 * PR 2, Task 9): it is a `dropdown` menu cloned from the server
 * `<template data-gantt-add-menu>`, whose items open the form drawer themselves
 * (`slide-over-trigger`, like the toolbar « Ajouter »).
 *
 * Targets:
 *   main             — the static panel body (hidden while composing)
 *   rescheduleCompose — the in-panel reschedule compose view
 *   frame            — the <turbo-frame> the phase path is lazy-loaded into
 *   loadingTemplate  — (optional) a <template> whose content (a loading skeleton)
 *                      replaces the frame's children on each open so the previous
 *                      phase never flashes while the new one loads
 *
 * Values:
 *   pathTemplate — the phase show path with an `:id` placeholder, substituted
 *                  with the selected row id (e.g. "/projects/123/plan_phases/:id")
 *   panelId      — the Ui::SlideOverComponent root id to open (default "phase-panel")
 *   autoOpen     — a phase row id to open automatically on connect (deep-link from
 *                  a Synthèse row: ?phase=<id> → open that phase's overlay on arrival)
 *
 * HOST WIRING (the next layer renders this): register both controllers on a
 * shared ancestor and connect them declaratively — no listener is added in
 * connect(), so there is nothing to tear down in disconnect():
 *   <div data-controller="gantt phase-panel"
 *        data-action="gantt:phase-selected->phase-panel#openFromGantt"
 *        data-phase-panel-path-template-value="/projects/123/plan_phases/:id">
 *     <%= render Ui::GanttChartComponent.new(..., phase_select: :event) %>
 *     <%= render Ui::SlideOverComponent.new(id: "phase-panel") do %>
 *       <turbo-frame id="phase-panel-frame" data-phase-panel-target="frame"></turbo-frame>
 *     <% end %>
 *   </div>
 *
 * openFromGantt(e) — the Gantt dispatches gantt:phase-selected for LEAF bars,
 * and (architect host only, gantt `group-select` value) for LOT rows too — the
 * server branches /plan_phases/:id on kind and serves the matching panel.
 * It points the panel turbo-frame's src at the phase path for e.detail.rowId,
 * then opens the slide-over by dispatching `slide-over:open` on its root element
 * (the drawer's own open API — see slide_over_controller.js / slide_over_trigger).
 */
export default class extends Controller {
  static targets = ['main', 'rescheduleCompose', 'frame', 'loadingTemplate'];
  static values = {
    pathTemplate: String,
    panelId: { type: String, default: 'phase-panel' },
    autoOpen: String,
  };

  // Deep-link auto-open: a Synthèse row links to ?view=gantt&phase=<id>; the Gantt
  // host renders this controller with `auto-open-value="<id>"`, so on connect we
  // open that phase's overlay. Deferred to the next frame so the sibling
  // slide-over controller has connected and is listening for `slide-over:open`.
  connect() {
    if (!this.hasAutoOpenValue || !this.autoOpenValue) return;
    const rowId = this.autoOpenValue;
    requestAnimationFrame(() => this.openPhase(rowId));
  }

  // gantt:phase-selected → lazy-load the selected phase into the panel frame,
  // then open the slide-over. The Gantt dispatches for LEAF bars, and (architect
  // host only, gantt `group-select` value) for LOT rows too — the server
  // branches /plan_phases/:id on kind and serves the matching panel.
  openFromGantt(event) {
    const rowId = event?.detail?.rowId;
    if (rowId == null || rowId === '') return; // group rows / malformed event
    this.openPhase(rowId);
  }

  // Point the panel turbo-frame's src at the phase path and open the slide-over.
  // Robust to the frame or the slide-over not being on the page (no-op rather
  // than throw).
  openPhase(rowId) {
    if (rowId == null || rowId === '') return;

    if (this.hasFrameTarget && this.hasPathTemplateValue && this.pathTemplateValue) {
      const nextSrc = this.pathTemplateValue.replace(':id', String(rowId));
      // A turbo-frame keeps its PREVIOUS children until the new response lands,
      // so opening the panel before the fetch resolves briefly flashed the prior
      // phase. Only act when the target src actually CHANGES (a different phase):
      // drop the stale content + show the loading skeleton, THEN set the new src.
      // When the src is unchanged (re-opening the SAME phase) we keep the current
      // content untouched — Turbo would not re-fetch an identical src, so clearing
      // it would strand the panel on the skeleton forever.
      if (nextSrc !== this.frameTarget.getAttribute('src')) {
        this.resetFrameToLoading();
        this.frameTarget.setAttribute('src', nextSrc);
      }
    }

    const drawer = document.getElementById(this.panelIdValue);
    if (drawer) drawer.dispatchEvent(new CustomEvent('slide-over:open'));
  }

  // Drop the stale phase content immediately so it can never flash while the new
  // phase loads, swapping in the server-rendered loading skeleton when present
  // (cloned from the <template> target — keeps the markup lint-safe in ERB rather
  // than authored in JS). Falls back to an empty frame if no template is mounted.
  resetFrameToLoading() {
    if (this.hasLoadingTemplateTarget) {
      this.frameTarget.replaceChildren(this.loadingTemplateTarget.content.cloneNode(true));
    } else {
      this.frameTarget.replaceChildren();
    }
  }

  openReschedule() {
    if (!this.hasRescheduleComposeTarget) return;
    this.previouslyFocused = document.activeElement;
    // The main body carries a static `flex` class; in Tailwind v4 that display
    // utility wins over `hidden`, so remove it alongside (mirror the compose pane).
    this.mainTarget.classList.add('hidden');
    this.mainTarget.classList.remove('flex');
    this.rescheduleComposeTarget.classList.remove('hidden');
    this.rescheduleComposeTarget.classList.add('flex');
    // Focus d'ENTRÉE seulement — ce panneau ne piège pas la tabulation (il vit
    // dans un slide-over qui, lui, porte le piège) : on ne consomme donc que la
    // liste canonique, pas `trapFocus`. `visibleFocusables` ajoute au passage le
    // filtre de visibilité : un premier contrôle encore caché ne capte plus le
    // focus, et un `select`/`textarea` désactivé n'est plus une cible d'entrée.
    // Le PREMIER CHAMP, pas le premier focusable : la barre d'action du volet
    // (bouton « Retour ») précède les champs dans le DOM, et l'utilisateur qui
    // ouvre « Reprogrammer » vient saisir une date, pas revenir en arrière
    // (mesuré en QA — le focus tombait sur « Retour », déjà avant l'extraction).
    const focusables = visibleFocusables(this.rescheduleComposeTarget);
    const focusable =
      focusables.find((el) => el.matches('input, select, textarea')) || focusables[0];
    if (focusable) focusable.focus();
  }

  closeReschedule() {
    if (!this.hasRescheduleComposeTarget) return;
    this.rescheduleComposeTarget.classList.add('hidden');
    this.rescheduleComposeTarget.classList.remove('flex');
    this.mainTarget.classList.remove('hidden');
    this.mainTarget.classList.add('flex');
    if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
  }
}

// R-14 (Nicolas 2026-10-01) — appelé par `planning/plan_phases/_close_phase_panel`
// (suppression d'un nœud, ou décalage résolu sur un nœud supprimé entre-temps, sur
// les hôtes architecte et entreprise) : le lien profond `?phase=` ne doit pas survivre
// au nœud qu'il ouvrait. Global de portée module, comme `window.closeSlideOver`
// (slide_over_controller.js), parce que le flux Turbo n'appelle qu'un script inline.
// Garde `typeof` : ce module est aussi importé sous `node --test` sans `window`
// (drawer_frame_new_src_reset.test.mjs).
if (typeof window !== 'undefined') {
  window.forgetPlanningPhaseParam = () => forgetPhaseParam(window);
}
